import { useCallback, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { HubOption } from 'dorian-shared/types/branch/HubOption';
import type { TagCondition, HubTagTemplate } from 'dorian-shared/types/branch/HubOption';
import { createDefaultTagCondition } from '../hubStepDefaults';

type OnChange = (changes: Partial<HubOption>) => void;
type Position = 'top' | 'bottom';

function conditionsKey(position: Position): 'tagTopConditions' | 'tagBottomConditions' {
  return position === 'top' ? 'tagTopConditions' : 'tagBottomConditions';
}

export function useTagConditions(
  conditions: TagCondition[],
  position: Position,
  onChange: OnChange,
  templates: HubTagTemplate[],
) {
  const key = conditionsKey(position);

  const addCondition = useCallback(
    (tagTemplateId: number) => {
      onChange({ [key]: [...conditions, createDefaultTagCondition(tagTemplateId)] });
    },
    [conditions, onChange, key],
  );

  const removeCondition = useCallback(
    (uuid: string) => {
      onChange({ [key]: conditions.filter((c) => c.uuid !== uuid) });
    },
    [conditions, onChange, key],
  );

  const updateCondition = useCallback(
    (uuid: string, updated: TagCondition) => {
      onChange({ [key]: conditions.map((c) => (c.uuid === uuid ? updated : c)) });
    },
    [conditions, onChange, key],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = conditions.findIndex((c) => c.uuid === active.id);
        const newIndex = conditions.findIndex((c) => c.uuid === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onChange({ [key]: arrayMove(conditions, oldIndex, newIndex) });
        }
      }
    },
    [conditions, onChange, key],
  );

  const summary = useMemo((): string => {
    if (conditions.length === 0) return '';

    return conditions
      .map((cond) => {
        const template = templates.find((t) => t.id === cond.tagTemplateId);
        return template?.text ?? template?.name ?? `tag #${cond.tagTemplateId}`;
      })
      .join(', ');
  }, [conditions, templates]);

  return {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
    summary,
  };
}
